import { useEffect, useRef, useState } from 'react';
import { adminAiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Sparkles,
  Send,
  Loader2,
  Copy,
  MessageCircle,
  PenSquare,
  BarChart3,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  Target,
  Store as StoreIcon,
} from 'lucide-react';

const TABS = [
  { id: 'chat', label: 'Consultoria', icon: MessageCircle },
  { id: 'content', label: 'Criar Conteúdo', icon: PenSquare },
  { id: 'insights', label: 'Central de Crescimento', icon: BarChart3 },
];

const CONTENT_TYPES = [
  { id: 'titulo', label: 'Títulos' },
  { id: 'subtitulo', label: 'Subtítulos' },
  { id: 'descricao', label: 'Descrição de produto' },
  { id: 'slogan', label: 'Slogans' },
  { id: 'cta', label: 'CTAs (Chamadas)' },
  { id: 'campanha', label: 'Campanha completa' },
  { id: 'promocao', label: 'Promoção' },
  { id: 'whatsapp', label: 'Post WhatsApp' },
  { id: 'instagram', label: 'Post Instagram' },
  { id: 'facebook', label: 'Post Facebook' },
  { id: 'google', label: 'Google Ads' },
  { id: 'vitrine', label: 'Vitrine da loja' },
  { id: 'calendario', label: 'Calendário promocional' },
];

const TONES = [
  { id: 'vendedor', label: 'Vendedor' },
  { id: 'profissional', label: 'Profissional' },
  { id: 'descontraido', label: 'Descontraído' },
  { id: 'premium', label: 'Premium' },
];

const SESSION_KEY = 'destack_admin_ai_session';

const ensureSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = `adm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
};

export const IADestackVendas = () => {
  const [tab, setTab] = useState('chat');
  return (
    <div className="space-y-5" data-testid="ia-destack-page">
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-violet-900 text-white p-6 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute -right-16 -bottom-8 w-56 h-56 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Sparkles className="w-7 h-7" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-200">Central Inteligente Exclusiva</p>
            <h1 className="text-2xl font-bold tracking-tight">IA DESTACK Vendas</h1>
            <p className="text-sm text-blue-100 mt-0.5">
              Sua consultora estratégica de marketing e vendas.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                active ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'chat' && <ConsultoriaChat />}
      {tab === 'content' && <ContentCreator />}
      {tab === 'insights' && <InsightsPanel />}
    </div>
  );
};

// ============== CONSULTORIA (Chat) ==============
const ConsultoriaChat = () => {
  const [sessionId] = useState(ensureSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    adminAiAPI.history(sessionId).then(({ data }) => setMessages(data.messages || [])).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const suggestions = [
    'Quais lojas precisam de mais divulgação?',
    'Que campanha devo criar esta semana?',
    'Como aumentar o ticket médio?',
    'Quais produtos têm maior potencial?',
  ];

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content }]);
    setSending(true);
    try {
      const { data } = await adminAiAPI.chat(sessionId, content);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: err.response?.data?.detail || 'Não consegui responder agora.',
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div ref={scrollRef} className="h-[520px] overflow-y-auto p-5 space-y-3 bg-slate-50">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <Lightbulb className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="text-slate-800 font-semibold">Sou sua consultora de marketing e vendas</p>
              <p className="text-slate-500 text-sm mt-1 mb-4">Pergunte qualquer coisa sobre o desempenho da plataforma.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl mx-auto">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-left text-sm px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-700 transition-colors"
                    data-testid={`admin-ai-sugg-${s.slice(0, 8)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === 'user'
                  ? 'bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-br-sm'
                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
              }`}>{m.content}</div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2 shadow-sm">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="p-3 border-t border-slate-200 bg-white flex gap-2"
        >
          <Textarea
            rows={1}
            placeholder="Pergunte à IA DESTACK Vendas..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="resize-none min-h-[42px] max-h-32"
            data-testid="admin-ai-input"
          />
          <Button type="submit" disabled={sending || !input.trim()} className="bg-gradient-to-br from-blue-600 to-violet-600 h-auto" data-testid="admin-ai-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ============== CONTENT CREATOR ==============
const ContentCreator = () => {
  const [type, setType] = useState('descricao');
  const [tone, setTone] = useState('vendedor');
  const [topic, setTopic] = useState('');
  const [extras, setExtras] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error('Descreva o tema, produto ou ideia');
      return;
    }
    setLoading(true);
    setOutput('');
    try {
      const { data } = await adminAiAPI.content({ content_type: type, topic, tone, extras });
      setOutput(data.content);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao gerar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    toast.success('Conteúdo copiado!');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base">Gerar Conteúdo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label>Tipo de conteúdo</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="content-type"
            >
              {CONTENT_TYPES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Tom da comunicação</Label>
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTone(t.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                    tone === t.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`tone-${t.id}`}
                >{t.label}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Tema / Produto / Ideia</Label>
            <Input
              placeholder="Ex: kit de alimentos família, promoção de Dia das Mães..."
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="content-topic"
            />
          </div>
          <div className="space-y-2">
            <Label>Detalhes extras (opcional)</Label>
            <Textarea
              rows={2}
              placeholder="Ex: com 15% de desconto, público jovem..."
              value={extras}
              onChange={(e) => setExtras(e.target.value)}
              data-testid="content-extras"
            />
          </div>
          <Button
            onClick={generate}
            disabled={loading || !topic.trim()}
            className="w-full bg-gradient-to-br from-blue-600 to-violet-600"
            data-testid="content-generate-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Gerar com IA
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base">Resultado</CardTitle>
          {output && (
            <Button onClick={copy} size="sm" variant="outline" data-testid="content-copy-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {output ? (
            <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans bg-slate-50 border border-slate-200 rounded-xl p-4 min-h-[300px]" data-testid="content-output">
              {output}
            </pre>
          ) : (
            <div className="text-center py-16 text-slate-400 text-sm bg-slate-50 border border-dashed border-slate-200 rounded-xl min-h-[300px] flex items-center justify-center">
              O conteúdo gerado aparecerá aqui
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== INSIGHTS ==============
const InsightsPanel = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await adminAiAPI.insights(d);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(days); }, [days]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-full">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                days === d ? 'bg-white text-slate-900 shadow' : 'text-slate-500'
              }`}
              data-testid={`insights-period-${d}`}
            >{d} dias</button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => load(days)} disabled={loading} data-testid="insights-reload">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" /></div>
      ) : !data ? (
        <p className="text-center text-slate-500 py-8">Sem dados disponíveis.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* AI recommendations */}
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Diagnóstico & Sugestões da IA
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.ai_recommendations ? (
                <div className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed" data-testid="ai-recommendations">
                  {data.ai_recommendations}
                </div>
              ) : (
                <p className="text-sm text-slate-500">A IA não gerou recomendações no momento.</p>
              )}
            </CardContent>
          </Card>

          {/* Top stores */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Top Lojas do Período
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_stores?.length ? (
                <ul className="space-y-2">
                  {data.top_stores.map((s, i) => (
                    <li key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50" data-testid={`top-store-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-xs">
                          {i + 1}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{s.store_name}</p>
                          <p className="text-xs text-slate-500">{s.orders} pedido(s)</p>
                        </div>
                      </div>
                      <p className="font-bold text-emerald-700 text-sm">R$ {s.revenue.toFixed(2)}</p>
                    </li>
                  ))}
                </ul>
              ) : <EmptyState msg="Sem vendas no período" />}
            </CardContent>
          </Card>

          {/* Inactive stores */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingDown className="w-5 h-5 text-rose-600" />
                Lojas Ativas SEM Vendas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.inactive_stores?.length ? (
                <ul className="space-y-2">
                  {data.inactive_stores.map((s, i) => (
                    <li key={i} className="flex items-center justify-between p-3 rounded-lg bg-rose-50 border border-rose-100" data-testid={`inactive-store-${i}`}>
                      <div className="flex items-center gap-2">
                        <StoreIcon className="w-4 h-4 text-rose-500" />
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{s.name}</p>
                          <p className="text-xs text-slate-500">{s.city}</p>
                        </div>
                      </div>
                      <Target className="w-4 h-4 text-rose-500" />
                    </li>
                  ))}
                </ul>
              ) : <EmptyState msg="Todas as lojas ativas venderam. Ótimo trabalho!" />}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ msg }) => (
  <div className="text-center py-6 text-slate-400 text-sm">{msg}</div>
);

export default IADestackVendas;
